import { createSelector, createSlice } from '@reduxjs/toolkit';

function formatDate(isoString) {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
function getFileType(fileName) {
  if (!fileName || typeof fileName !== 'string') return '';
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0) return '';
  return fileName.slice(lastDotIndex + 1);
}

const initialState = {
  isLoading: false,
  isActive: false,
  isAdded: false,
  id: 0,
  uid: 0,
  sid: 0,
  profileId: 0,
  blockContent: {
    type: 'File',
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },
    items: [],
  },
  blockPreview: {
    isActive: false,
    view: 'compact',
    thumbnail: null,
    title: '',
    description: '',
    button: '',
    label: '',
    timeStart: null,
  },
  blockDesign: {
    containerDesign: {
      headingTitle: '',
      headingImage: '',
      headingTextAlign: 'left',
      headingDescription: '',
      headingDivider: '',
      sectionIsBackground: false,
      sectionTextColor: '#000000',
      sectionShadow: 'soft',
      sectionShadowColor: '#000',
      sectionShadowBorderColor: '#000',
      sectionBorderColor: '#000',
      sectionShape: 'round',
      sectionOpacity: 0,
      sectionBackgroundColor: '#ffffff',
      sectionIsFullWidth: false,
      sectionPadding: 20,
      sectionMargin: 12,
      sectionBackgroundType: 1,
      sectionBackgroundImage: '',
      sectionGradientColor1: '#B3F7EF',
      sectionGradientColor2: '#F7D6E0',
      sectionGradientDirection: 0,
      sectionGradientOffset: 0,
      sectionBorder: 'none',
    },

    itemDesign: {
      itemShadow: 'none',
      itemShadowColor: '#000',
      itemBorder: 'none',
      itemBorderColor: '#000',
      itemShape: 'round',
      itemOpacity: 0,
      itemTextColor: '#000',
      itemBackgroundColor: '#ffffff',
    },
    previewDesign: {},
  },
};
export const fileBlockSlice = createSlice({
  name: 'file',
  initialState,
  reducers: {
    // Core states
    fileUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    fileUpdateIsAdded: (state, action) => {
      state.isAdded = action.payload;
    },
    fileUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },

    // post file block
    initFileBlock: (state, action) => {
      const { id, uid, sid, type } = action.payload;
      state.id = id ?? 0;
      state.uid = uid ?? 0;
      state.sid = sid ?? 0;
      ((state.profileId = profileId ?? 0), (state.blockContent.type = type ?? 'File'));
      state.isActive = true;
      state.isLoading = false;
    },

    // clear file block
    clearFileBlock: () => initialState,

    updateFileBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },

    // Restore / store full block
    fileStore: (state, action) => {
      const payload = action.payload ?? {};
      const { id, isActive, sid, uid, profileId, data, preview } = payload;
      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, containerDesign, previewDesign } = blockDesign;
      state.isLoading = false;
      state.isActive = isActive;
      state.id = id ?? state.id;
      state.uid = uid ?? state.uid;
      state.sid = sid ?? state.sid;
      state.profileId = profileId ?? state.profileId;

      state.blockContent = {
        items: blockContent.items ?? [],
        heading: {
          ...blockContent.heading,
          title: blockContent.heading.title ?? '',
          description: blockContent.heading.description ?? '',
          textAlign: blockContent.heading.textAlign ?? 'left',
          image: blockContent.heading.image ?? '',
          divider: blockContent.heading.divider ?? '',
          position: blockContent.heading.position ?? '',
        },
      };
      state.blockDesign = {
        itemDesign: itemDesign ?? {},
        containerDesign: containerDesign ?? {},
        previewDesign: previewDesign ?? {},
      };
      state.blockPreview = {
        isActive: preview?.isActive || false,
        thumbnail: preview?.thumbnail ?? null,
        view: preview?.view ?? 'compact',
        title: preview?.title ?? '',
        description: preview?.description ?? '',
        button: preview?.button ?? '',
        label: preview?.label ?? '',
        timeStart: preview?.timeStart ?? null,
      };
    },
    // update block heading
    updateHeading: (state, action) => {
      const updatedHeading = action.payload;
      state.blockContent.heading = {
        ...state.blockContent.heading,
        ...updatedHeading,
      };
    },

    // file adding
    addFile: (state, action) => {
      const newFile = action.payload;
      if (!state.blockContent) {
        state.blockContent = { items: [] };
      }
      if (!Array.isArray(state.blockContent.items)) {
        state.blockContent.items = [];
      }
      state.blockContent.items.push({
        id: newFile.id ?? Date.now(),
        file: newFile.fileObj?.fileUrl ?? null,
        fileName: newFile.title ?? '',
        fileSize: newFile.fileObj.fileSizeFormatted ?? 0,
        uploadDate: formatDate(newFile.fileObj.uploadDate) ?? new Date().toISOString(),
        fileType: getFileType(newFile.fileObj.fileName) ?? '',
        accessType: newFile.accessType ?? 'public',
        isActive: newFile.isActive ?? false,
      });
    },

    // single file update
    updateFile: (state, action) => {
      const updatedFile = action.payload;
      const index = state.blockContent.items.findIndex((f) => f.id === updatedFile.id);
      if (index !== -1) {
        state.blockContent.items[index] = {
          ...state.blockContent.items[index],
          id: updatedFile.id ?? state.blockContent.items[index].id,
          file: updatedFile.fileObj?.fileUrl ?? state.blockContent.items[index].file,
          fileName: updatedFile.title ?? state.blockContent.items[index].fileName,
          fileSize: updatedFile.fileObj?.fileSizeFormatted ?? state.blockContent.items[index].fileSize,
          uploadDate: formatDate(updatedFile.fileObj?.uploadDate) ?? formatDate(state.blockContent.items[index].uploadDate),
          fileType: updatedFile.fileType ?? state.blockContent.items[index].fileType,
          accessType: updatedFile.accessType ?? state.blockContent.items[index].accessType,
          isActive: updatedFile.isActive ?? state.blockContent.items[index].isActive,
        };
      }
    },

    // single file remove
    removeFile: (state, action) => {
      const fileId = action.payload;
      state.blockContent.items = state.blockContent.items.filter((f) => f.id !== fileId);
    },

    // update design
    updateDesign: (state, action) => {
      const { itemDesign, containerDesign, previewDesign } = action.payload;
      if (itemDesign) state.blockDesign.itemDesign = itemDesign;
      if (containerDesign) state.blockDesign.containerDesign = containerDesign;
      if (previewDesign) state.blockDesign.previewDesign = previewDesign;
    },
    fileUpdatePreviewActive: (state, action) => {
      state.blockPreview.isActive = action.payload;
    },
    fileUpdateBlockPreviewView: (state, action) => {
      state.blockPreview.view = action.payload;
    },
    fileUpdateBlockPreviewContent: (state, action) => {
      const { field, value } = action.payload;
      state.blockPreview[field] = value;
    },
    fileUpdateBlockPreviewDesign: (state, action) => {
      const { field, value } = action.payload;
      state.blockDesign.previewDesign[field] = value;
    },
  },
});

export const {
  fileUpdateIsLoading,
  fileUpdateIsAdded,
  fileUpdateIsActive,
  initFileBlock,
  clearFileBlock,
  updateFileBlockContent,
  updateHeading,
  fileStore,
  addFile,
  updateFile,
  removeFile,
  fileUpdateDesign,
  fileUpdatePreviewActive,
  fileUpdateBlockPreviewView,
  fileUpdateBlockPreviewContent,
  fileUpdateBlockPreviewDesign,
} = fileBlockSlice.actions;

//  Selectors
export const selectFileIsLoading = (state) => state.file.isLoading;
export const selectFileIsAdded = (state) => state.file.isAdded;
export const selectFileIsActive = (state) => state.file.isActive;
export const selectFileId = (state) => state.file.id;
export const selectFileUID = (state) => state.file.uid;
export const selectFileSID = (state) => state.file.sid;
export const selectProfileId = (state) => state.file.profileId;
export const selectFileBlockContent = (state) => state.file.blockContent;
export const selectFileBlockHeading = (state) => state.file.blockContent.heading;
export const selectFileList = createSelector([selectFileBlockContent], (blockContent) => blockContent?.items ?? []);
export const selectFileBlockDesign = (state) => state.file.blockDesign;
export const selectFileItemDesign = (state) => state.file.blockDesign.itemDesign;
export const selectFileContainerDesign = (state) => state.file.blockDesign.containerDesign;
export const selectFilePreviewContent = (state) => state.file.blockPreview;
export const selectFilePreviewDesign = (state) => state.file.blockDesign.previewDesign;
export const selectFilePreviewActive = (state) => state.file.blockPreview.isActive;
export const selectFilePreviewView = (state) => state.file.blockPreview.view;

export default fileBlockSlice.reducer;
